import { Injectable } from '@angular/core';

export interface NavigationBlockOptions {
  expectRouteChange?: boolean;
}

export type NavigationBlocker = (
  action?: () => void | Promise<void>,
  options?: NavigationBlockOptions
) => Promise<boolean>;

@Injectable({
  providedIn: 'root'
})
export class NavigationBlockService {
  private readonly blockers = new Set<NavigationBlocker>();

  register(blocker: NavigationBlocker): void {
    this.blockers.add(blocker);
  }

  clear(blocker: NavigationBlocker): void {
    this.blockers.delete(blocker);
  }

  /**
   * Runs the given action if no blockers prevent it.
   * If a blocker is registered, it receives the action and decides whether to
   * execute it (e.g. after a user confirmation dialog).
   * Returns true if the action was (or will be) executed, false if blocked.
   */
  async runWhenAllowed(
    action: () => void | Promise<void>,
    options?: NavigationBlockOptions
  ): Promise<boolean> {
    const blockerList = Array.from(this.blockers);

    if (blockerList.length === 0) {
      await action();
      return true;
    }

    // In normal use only one blocker is active at a time.
    // The most recently registered blocker has precedence; it controls
    // whether the action runs (and is responsible for calling it).
    return await blockerList[blockerList.length - 1](action, options);
  }
}
